import {
  BoundDateField,
  BoundNumberField,
  BoundTextAreaField,
  BoundTextField,
  Button,
  column,
  Css,
  emptyCell,
  GridColumn,
  GridDataRow,
  GridTable,
  numericColumn,
  simpleHeader,
  StaticField,
  TabsWithContent,
  Tag,
  Tooltip,
  useComputed,
  useTestIds,
} from "@homebound/beam";
import { sentenceCase } from "change-case";
import { Observer } from "mobx-react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { createInvoicesUrl, createProjectScheduleUrl } from "src/RouteUrls";
import { CommentFeed, FormActions, FormMode, HistoryFeed, PdfViewer, priceCell, priceTotal } from "src/components";
import { InvoiceNumber } from "src/components/formFields/InvoiceNumber";
import {
  CostClassificationType,
  InvoiceStatus,
  LotType,
  SaveInvoiceInput,
  SaveInvoiceLineItemInput,
} from "src/generated/graphqlTypes";
import { useModeParam } from "src/hooks";
import { useRouteParams } from "src/hooks/useRouteParams";
import { useStripStage } from "src/hooks/useStripStage";
import { ProjectStageDetailFragment } from "src/queries/fragments.generated";
import { useApprovalSuperDrawer } from "src/routes/components/Approval/ApprovalSuperDrawer";
import { invoiceStatusToTagType } from "src/routes/invoices/InvoicesPage";
import { PageHeader, PageHeaderActions } from "src/routes/layout/PageHeader";
import { RelatedContractBoundSelectField } from "src/routes/projects/components/RelatedContractBoundSelectField";
import {
  InvoiceEditor_InvoiceFragment,
  InvoiceEditQuery,
  useDeleteInvoiceMutation,
  useInvoiceEditQuery,
  useSaveInvoiceMutation,
} from "src/routes/projects/invoices/InvoiceEditor.generated";
import { ProjectParams } from "src/routes/routesDef";
import { approvalStatusToTagTypeMapper, fail, formatCentsToPrice, isDefined, queryResult, sum } from "src/utils";
import { formatWithShortYear, formatWithYear } from "src/utils/dates";
import { FieldState, ObjectConfig, ObjectState, required, useFormState } from "src/utils/formState";
import { Temporal } from "temporal-polyfill";
import { useProjectContext } from "../context/ProjectContext";
import { InvoiceBanner } from "./invoice-v2/InvoiceBanner";

export function InvoiceEditor() {
  useStripStage();
  const { projectId } = useRouteParams<ProjectParams>();
  const { lotType } = useProjectContext();
  const { mode, id, onCancel, onEdit, onSave } = useModeParam({ listPageUrl: createInvoicesUrl(projectId) });
  const query = useInvoiceEditQuery({ variables: { invoiceId: id!, includeLineItems: lotType === LotType.Boyl } });
  const [saveInvoice] = useSaveInvoiceMutation();
  const [deleteInvoice] = useDeleteInvoiceMutation();

  const handleSave = async (input: SaveInvoiceInput | undefined, redirect?: boolean) => {
    if (input) {
      await saveInvoice({ variables: { input } });
    }
    if (redirect) {
      onSave();
    } else {
      await query.refetch();
    }
  };

  const handleDelete = async () => {
    await deleteInvoice({
      variables: { input: { id: id || fail("Attempting to delete invoice without an invoice id") } },
    });
    onSave();
  };

  return queryResult(query, ({ invoice }) => (
    <>
      <PageHeader title={invoice.name} breadcrumb={{ label: "All Invoices", href: createInvoicesUrl(projectId) }} />
      <InvoiceEditorView
        invoice={invoice}
        project={invoice.projectStage.project}
        projectStages={invoice.projectStage.project.stages}
        mode={mode}
        onCancel={onCancel}
        onEdit={onEdit}
        onSave={handleSave}
        onDelete={handleDelete}
      />
    </>
  ));
}

type InvoiceEditorInvoice = NonNullable<InvoiceEditQuery["invoice"]>;

// Export for testing
export type InvoiceEditorViewProps = {
  mode: FormMode;
  project: InvoiceEditor_InvoiceFragment["projectStage"]["project"];
  projectStages: ProjectStageDetailFragment[];
  invoice: InvoiceEditorInvoice;
  onCancel: () => void;
  onEdit: () => void;
  onSave: (formState: SaveInvoiceInput | undefined, redirect?: boolean) => Promise<void>;
  onDelete: () => Promise<void>;
};

export function InvoiceEditorView(props: InvoiceEditorViewProps) {
  const { project, projectStages, invoice, mode, onSave, onEdit, onDelete, onCancel } = props;
  const testIds = useTestIds({}, "invoiceEditor");
  const { clientNoun } = useProjectContext();
  const openApproval = useApprovalSuperDrawer();
  const readOnly = mode === "read";
  const [tab, setTab] = useState("details");
  const { primaryHomeowner, customerOwnerEntity } = invoice.projectStage.project;

  const formState = useFormState({
    config: formConfigInvoiceEditor,
    init: { input: invoice, map: (invoice) => mapToFormInvoiceEditor(invoice) },
    readOnly,
  });

  const editableLineItemRows = useComputed(() => {
    if (mode === "read") return [];
    return formState.lineItems.rows.filter((r) =>
      invoice.displayLineItems.some((dil) => dil.id === r.id.value && dil.canEdit.allowed),
    );
  }, [mode, invoice, formState]);
  const canEditLineItems = editableLineItemRows.nonEmpty;
  const lineItemRows = useComputed(
    () => createLineItemRows(invoice.displayLineItems, editableLineItemRows),
    [invoice, editableLineItemRows],
  );

  return (
    <Observer>
      {() => (
        <>
          <PageHeaderActions>
            <>
              <FormActions
                {...{ mode, onEdit, onCancel, formState, onDelete }}
                entityType="Invoice"
                entityName={`Invoice ${invoice?.id}`}
                secondaryVariant="tertiary"
                // TODO: replace with a potential operation
                deleteDisabledReason={deleteDisabledReason(invoice, project)}
                onSave={() => onSave(buildSaveInput(formState, canEditLineItems), true)}
              />
              {invoice.pandaDocs.first?.externalPandaDocUrl && (
                <Button label="View in PandaDoc" onClick={invoice.pandaDocs.first.externalPandaDocUrl} />
              )}
            </>
          </PageHeaderActions>
          {mode === "read" && <InvoiceBanner invoiceId={invoice.id} hasSyncError={Boolean(invoice.intacctSyncError)} />}
          <div css={Css.df.gap2.$}>
            <div css={Css.w50.$}>
              <div css={Css.bgWhite.p3.br8.bshBasic.$}>
                <InvoiceStatusTag invoice={invoice} />
                <div css={Css.df.pt2.lg.$}>
                  <div>{invoice.name}</div>
                  <div css={Css.mla.$}>{formatCentsToPrice(invoice.amountInCents)}</div>
                </div>
                <div css={Css.mb1.df.if(mode !== "read").my2.$}>
                  <div css={Css.maxw75.oh.$}>
                    <BoundTextField field={formState.title} labelStyle={mode === "read" ? "hidden" : "inline"} />
                  </div>
                  <div css={Css.smSb.ta("right").mla.mya.$}>
                    Paid amount: {formatCentsToPrice(invoice.amountPaidInCents)}
                  </div>
                </div>

                <TabsWithContent
                  tabs={[
                    {
                      name: "Details",
                      value: "details",
                      render: () => (
                        <div>
                          {invoice.intacctSyncError && (
                            <StaticField label="Sync Error">
                              <div css={Css.red700.$}> {invoice.intacctSyncError} </div>
                            </StaticField>
                          )}
                          <div css={Css.df.w100.py2.$}>
                            {isDefined(formState.projectStageId.value) && (
                              <div css={Css.w25.$}>
                                <InvoiceNumber
                                  formState={formState}
                                  isHistoricalInvoiceNumber={invoice?.isHistoricalInvoiceNumber}
                                  mode={mode}
                                  stage={invoice.projectStage.stage.code}
                                />
                              </div>
                            )}
                            <div css={Css.w25.if(mode !== "read").pr2.$}>
                              <BoundDateField
                                field={formState.invoiceDate}
                                helperText="Enter the date of this invoice"
                              />
                            </div>
                            <div css={Css.w25.if(mode !== "read").pr2.$}>
                              <BoundDateField
                                field={formState.dueDate}
                                helperText={`Enter the date the ${clientNoun.toLowerCase()} needs to pay this invoice by`}
                              />
                            </div>
                            {invoice.paidDate && <StaticField label="Paid" value={formatWithYear(invoice.paidDate)} />}
                          </div>
                          <div css={Css.df.jcsb.$}>
                            <div css={Css.mw25.$}>
                              <StaticField value={invoice.intacctId ?? ""} label="Intacct ID" />
                            </div>
                            <div css={Css.mw25.$}>
                              <StaticField value={invoice.source.name} label="Source" />
                            </div>
                            <Tooltip
                              title="Cannot change the related contract on an existing invoice"
                              disabled={!invoice}
                            >
                              <RelatedContractBoundSelectField
                                field={formState.projectStageId}
                                projectStages={projectStages}
                                readOnly={Boolean(invoice) || mode === "read"}
                                helperText={mode === "read" ? undefined : "Only signed contracts can be selected"}
                              />
                            </Tooltip>
                            <BoundTextAreaField
                              field={formState.description}
                              label={`Memo${mode !== "read" ? " (optional)" : ""}`}
                              helperText={`This will be visible to the ${clientNoun.toLowerCase()}`}
                            />
                          </div>
                          <InvoiceBillTo
                            customerOwnerEntity={customerOwnerEntity}
                            primaryHomeowner={primaryHomeowner}
                            testIds={testIds}
                          />
                          <InvoiceParentDetails invoice={invoice} project={project} testIds={testIds} />
                          <InvoiceApproval invoice={invoice} openApproval={openApproval} testIds={testIds} />
                        </div>
                      ),
                    },
                    { name: "Comments", value: "comments", render: () => <CommentFeed commentable={invoice} /> },
                    { name: "History", value: "history", render: () => <HistoryFeed historyItems={invoice.history} /> },
                  ]}
                  selected={tab}
                  onChange={setTab}
                />
              </div>
              <div css={Css.mt2.p2.bgWhite.bshBasic.br8.$}>
                <GridTable
                  stickyHeader
                  style={{ allWhite: true, inlineEditing: canEditLineItems }}
                  columns={createLineItemColumns(invoice.projectStage.project.lotType.code, testIds.contractItem)}
                  rows={lineItemRows}
                />
              </div>
            </div>
            {invoice.document && (
              <div css={Css.bgWhite.w50.bshBasic.oa.br8.$}>
                <PdfViewer hasHeader assets={[invoice.document.asset]} />
              </div>
            )}
          </div>
        </>
      )}
    </Observer>
  );
}

type InvoiceEditorTestIds = ReturnType<typeof useTestIds>;
type InvoiceProject = InvoiceEditorViewProps["project"];

function InvoiceStatusTag(props: { invoice: InvoiceEditor_InvoiceFragment }) {
  const { invoice } = props;
  return invoice.isHistorical ? (
    <Tag type="info" text="HISTORICAL" />
  ) : (
    <Tag type={invoiceStatusToTagType[invoice.status.code]} text={invoice.status.name} />
  );
}

function InvoiceBillTo(props: {
  customerOwnerEntity: InvoiceProject["customerOwnerEntity"];
  primaryHomeowner: InvoiceProject["primaryHomeowner"];
  testIds: InvoiceEditorTestIds;
}) {
  const { customerOwnerEntity, primaryHomeowner, testIds } = props;
  return (
    <div {...testIds.billTo} css={Css.mb3.$}>
      <div css={Css.gray700.mbPx(2).$}>Bill To</div>
      <div css={Css.smSb.$}>
        {customerOwnerEntity?.name ??
          (primaryHomeowner && (
            <div>
              <div>{primaryHomeowner.fullName}</div>
              {primaryHomeowner.address && (
                <>
                  <div>{primaryHomeowner.address.street1}</div>
                  <div>
                    {`${primaryHomeowner.address.city}, ${primaryHomeowner.address.state} ${primaryHomeowner.address.postalCode}`}
                  </div>
                </>
              )}
              <Link to={`mailto:${primaryHomeowner.email}`}>{primaryHomeowner.email}</Link>
            </div>
          )) ??
          "Client"}
      </div>
    </div>
  );
}

function InvoiceParentDetails(props: {
  invoice: InvoiceEditor_InvoiceFragment;
  project: InvoiceProject;
  testIds: InvoiceEditorTestIds;
}) {
  const { invoice, project, testIds } = props;
  const { parent } = invoice;
  if (!parent) {
    return null;
  }
  return (
    <div {...testIds.parent} css={Css.mb3.$}>
      {"schedule" in parent ? (
        <>
          <div css={Css.sm.gray700.mb1.$}>Related Schedule Task</div>
          <div css={Css.br4.bgGray100.p2.$}>
            <div css={Css.df.jcsb.$}>
              <div css={Css.truncate.maxw50.mya.fw5.$}>
                <Link
                  data-testid="scheduleLink"
                  to={createProjectScheduleUrl(project.id, parent.schedule.stageDetail.code, parent.id)}
                  target="_blank"
                >
                  {parent.name}
                </Link>
              </div>
              <div css={Css.xs.mya.$}>
                <div>{sentenceCase(parent.statusDetail.code)}</div>
                {parent.completedAt && (
                  <div>
                    {
                      /* completedAt is supposed to be a Temporal.PlainDate, but at runtime it is actually
                       * a string. Apollo can't figure out which type policy to use on it because
                       * the types of completedAt on InvoiceParent differ between PlanTask /
                       * PlanMilestone and Task / ScheduleTask. So instead we need to parse the
                       * string ourselves then convert to Temporal.PlainDate and format it.  Meanwhile,
                       * tests *do* provide the correct type so we should just pass those through
                       */
                      formatWithShortYear(
                        typeof parent.completedAt === "string"
                          ? Temporal.PlainDate.from(parent.completedAt)
                          : parent.completedAt,
                      )
                    }
                  </div>
                )}
              </div>
              <div css={Css.xs.mwPx(120).mya.$}>{parent.assignee?.name}</div>
            </div>
          </div>
        </>
      ) : (
        parent.__typename === "PlanMilestone" && (
          <>
            <div css={Css.sm.gray700.mb1.$}>Related Plan Milestone</div>
            <div css={Css.br4.bgGray100.p2.$}>
              <div css={Css.df.jcsb.$}>
                <div css={Css.truncate.maxw50.mya.fw5.$}>{parent.name}</div>
                <div css={Css.xs.mya.$}>
                  <div>{sentenceCase(parent.taskStatusRollup.code)}</div>
                  {"completedDate" in parent && parent.completedDate && (
                    <div>{formatWithYear(parent.completedDate)}</div>
                  )}
                </div>
              </div>
            </div>
          </>
        )
      )}
    </div>
  );
}

function InvoiceApproval(props: {
  invoice: InvoiceEditor_InvoiceFragment;
  openApproval: ReturnType<typeof useApprovalSuperDrawer>;
  testIds: InvoiceEditorTestIds;
}) {
  const { invoice, openApproval, testIds } = props;
  return (
    <div {...testIds.approval}>
      <div css={Css.sm.gray700.mb1.$}>Approval</div>
      <div css={Css.br4.bgGray100.p2.$}>
        {invoice.approval && (
          <div css={Css.df.jcsb.$}>
            <Button variant="text" label={invoice.approval.name} onClick={() => openApproval(invoice.approval!.id)} />
            <Tag
              type={approvalStatusToTagTypeMapper(invoice.approval)[0]}
              text={approvalStatusToTagTypeMapper(invoice.approval)[1]}
            />
            <div css={Css.xs.mwPx(120).$}>{invoice.approval.requestedBy?.name}</div>
          </div>
        )}
      </div>
    </div>
  );
}

function deleteDisabledReason(invoice: InvoiceEditor_InvoiceFragment, project: InvoiceProject): string {
  if ([InvoiceStatus.Approved, InvoiceStatus.Paid].includes(invoice.status.code)) {
    return "Approved invoices cannot be deleted";
  }
  if (project.lotType.code === LotType.Bool) {
    return "BOOL invoices cannot be deleted";
  }
  return "";
}

type DisplayLineItem = InvoiceEditor_InvoiceFragment["displayLineItems"][number];

function allLineItems(invoice: InvoiceEditorInvoice) {
  return [...(invoice.lineItems ?? []), ...(invoice.scheduledLineItems ?? []).flatMap((isli) => isli.invoiceLineItems)];
}

type HeaderRow = { kind: "header" };
type LineItemRow = {
  kind: "line";
  data: DisplayLineItem & { amountField: FieldState<number | null | undefined> | undefined };
};
type TotalRow = { kind: "total"; data: TotalData };
type Row = HeaderRow | LineItemRow | TotalRow;
type TotalData = {
  totalAmountInCents: number;
  totalSoftCostInCents: number;
  totalHardCostInCents: number;
  totalMarkupInCents: number;
};

/** The cost (amount minus margin) for a single display line. */
function costInCents(line: DisplayLineItem): number {
  return line.amountInCents - (line.marginInCents ?? 0);
}

function createLineItemRows(
  displayLineItems: DisplayLineItem[],
  editableLineItemRows: readonly ObjectState<SaveInvoiceLineItemInput>[],
): GridDataRow<Row>[] {
  const lines = displayLineItems.map((line) => ({
    ...line,
    amountField: editableLineItemRows.find((r) => r.id.value === line.id)?.amountInCents,
  }));
  const totals: TotalData = {
    totalAmountInCents: lines.map((li) => li.amountField?.value ?? li.amountInCents).reduce(sum, 0),
    totalSoftCostInCents: lines
      .filter((li) => li.costClassificationType?.code === CostClassificationType.Soft)
      .map(costInCents)
      .reduce(sum, 0),
    totalHardCostInCents: lines
      .filter((li) => li.costClassificationType?.code === CostClassificationType.Hard)
      .map(costInCents)
      .reduce(sum, 0),
    totalMarkupInCents: lines.map((li) => li.marginInCents ?? 0).reduce(sum, 0),
  };

  return [
    simpleHeader,
    ...lines.map((data) => ({ kind: "line" as const, id: data.id, data })),
    { kind: "total" as const, id: "total", data: totals },
  ];
}

function createLineItemColumns(lotType: LotType, contractItemId: object): GridColumn<Row>[] {
  const nameColumn = column<Row>({
    header: () => "Project Item",
    line: (row) => <span {...contractItemId}>{row.name}</span>,
    total: () => "Totals",
    w: 3,
  });

  const softCostColumn = column<Row>({
    header: () => "Soft Costs",
    line: (row) =>
      row.costClassificationType?.code === CostClassificationType.Soft
        ? priceCell({ valueInCents: costInCents(row) })
        : emptyCell,
    total: (row) => priceTotal({ id: "softCostTotal", valueInCents: row.totalSoftCostInCents }),
  });

  const hardCostColumn = column<Row>({
    header: () => "Hard Costs",
    line: (row) =>
      row.costClassificationType?.code === CostClassificationType.Hard
        ? priceCell({ valueInCents: costInCents(row) })
        : emptyCell,
    total: (row) => priceTotal({ id: "hardCostTotal", valueInCents: row.totalHardCostInCents }),
  });

  const markupColumn = column<Row>({
    header: () => "Markup",
    line: (row) => (isDefined(row.marginInCents) ? priceCell({ valueInCents: row.marginInCents }) : emptyCell),
    total: (row) => priceTotal({ id: "markupTotal", valueInCents: row.totalMarkupInCents }),
  });

  const amountColumn = numericColumn<Row>({
    header: () => "This Invoice",
    line: (row) =>
      row.amountField ? (
        <BoundNumberField type="cents" field={row.amountField} />
      ) : (
        priceCell({ id: "thisInvoice", valueInCents: row.amountInCents })
      ),
    total: (row) => priceTotal({ id: "invoicedTotal", valueInCents: row.totalAmountInCents }),
  });

  return [
    nameColumn,
    // BOOL invoices break costs out by classification; other lot types only show the invoiced amount.
    ...(lotType === LotType.Bool ? [softCostColumn, hardCostColumn, markupColumn] : []),
    amountColumn,
  ];
}

type FormInput = Pick<
  SaveInvoiceInput,
  | "id"
  | "projectStageId"
  | "invoiceNumber"
  | "invoiceDate"
  | "dueDate"
  | "paidDate"
  | "description"
  | "title"
  | "creditForInvoiceId"
  | "lineItems"
>;

const formConfigInvoiceEditor: ObjectConfig<FormInput> = {
  id: { type: "value" },
  projectStageId: { type: "value", rules: [required] },
  invoiceNumber: { type: "value" },
  invoiceDate: { type: "value", rules: [required] },
  dueDate: { type: "value", rules: [required] },
  paidDate: { type: "value" },
  description: { type: "value" },
  title: { type: "value", rules: [required] },
  creditForInvoiceId: { type: "value" },
  lineItems: { type: "list", config: { id: { type: "value" }, amountInCents: { type: "value" } } },
};

function mapToFormInvoiceEditor(invoice: InvoiceEditorInvoice): FormInput {
  return {
    id: invoice.id,
    projectStageId: invoice.projectStage.id,
    invoiceNumber: invoice.invoiceNumber,
    invoiceDate: invoice.invoiceDate,
    dueDate: invoice.dueDate,
    paidDate: invoice.paidDate,
    description: invoice.description,
    title: invoice.title,
    lineItems: allLineItems(invoice).map((li) => ({ id: li.id, amountInCents: li.amountInCents })),
  };
}

/** saveInvoice treats `lineItems` as an exhaustive set, so once an amount is edited we resend every line. */
function buildSaveInput(formState: ObjectState<FormInput>, canEditLineItems: boolean): SaveInvoiceInput {
  const { lineItems, ...otherFields } = formState.value;
  return canEditLineItems && formState.lineItems.dirty ? { ...otherFields, lineItems } : otherFields;
}
